import React, { useState } from 'react'

import { Avatar, ButtonClean, Dropdown, Icon, Loading, Skeleton } from '@fourthwall/components'
import { ArrowShortDownIcon } from '@fourthwall/icons'
import type { GetTopCommentsResponse } from '@fourthwall/services/dist/MembershipsCreatorApiClient'
import { decodeHtmlEntities } from '@fourthwall/utils/dist/sanitize'

import { EventsSection } from '../../components/EventsSection/EventsSection'
import { useOverviewContext } from '../../hooks/useOverviewContext'
import { getJoinedDate } from '../../utils'
import { useTopCommentsQuery } from './hooks/useTopCommentsQuery'
import * as S from './styled'

type Range = GetTopCommentsResponse['range']

const RANGE_LABELS: Record<Range, string> = {
  '24_hours': 'Last 24 hours',
  '7_days': 'Last 7 days',
  '30_days': 'Last 30 days',
  year: 'Last year',
  all: 'All time',
}

export const TopComments = () => {
  const overview = useOverviewContext()

  const [range, setRange] = useState<Range>('7_days')
  const [isDefaultRange, setIsDefaultRange] = useState(true)

  const { topCommentsQuery } = useTopCommentsQuery([{ range }], { enabled: !isDefaultRange })

  if (overview.isLoading || overview.topComments === null || overview.membershipsUrl === null)
    return (
      <Skeleton>
        <Skeleton.Shape width="100%" height={256} />
      </Skeleton>
    )

  const comments = topCommentsQuery.data?.comments || overview.topComments

  const renderDropdown = () => {
    return (
      <Dropdown
        content={Object.keys(RANGE_LABELS).map((label) => {
          return (
            <Dropdown.Item
              label={(RANGE_LABELS as any)[label]}
              onClick={() => {
                setRange(label as Range)
                setIsDefaultRange(false)
              }}
            />
          )
        })}
      >
        <S.ButtonContainer>
          <ButtonClean label={RANGE_LABELS[range]} appearance="secondary" withUnderline={false} />
          <Icon component={ArrowShortDownIcon} height={10} />
        </S.ButtonContainer>
      </Dropdown>
    )
  }

  return (
    <Loading isLoading={topCommentsQuery.isFetching}>
      <EventsSection
        title="Top comments"
        button={renderDropdown()}
        emptyMessage={!comments.length ? "There's no comments yet." : undefined}
      >
        <S.Container>
          {comments.map(({ author, postedAt, post, path, body }) => {
            return (
              <S.Comment
                href={`${overview.membershipsUrl!.uri}${path}`}
                referrerPolicy="unsafe-url" // Memberships detects a creator auth context if /admin/dashboard is present in the Referrer header. The default browser Referrer policy is 'strict-origin-when-cross-origin', which removes the path for cross-origin requests (the Core dashboard is always on the internal Fourthwall domain). The `unsafe-url` policy preserves the full path
                target="_blank"
              >
                <S.Top>
                  <S.User>
                    <Avatar name={author.name} imageUrl={author.avatar || ''} size="small" />
                    {author.name}
                  </S.User>
                  <S.Date>{getJoinedDate(postedAt)}</S.Date>
                </S.Top>
                <S.Title>Re: {post.title}</S.Title>
                <S.Text
                  dangerouslySetInnerHTML={{
                    __html: decodeHtmlEntities(body, ['p', 'spoiler', 'img']),
                  }}
                />
              </S.Comment>
            )
          })}
        </S.Container>
      </EventsSection>
    </Loading>
  )
}
